import * as React from 'react'
import { useFastboundDiff, type FastboundCsvRowDiff } from './api/get-fastbound-diff'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Download, RefreshCw } from 'lucide-react'
import { DataTable } from '@/components/DataTable'
import { ColumnDef, type PaginationState } from '@tanstack/react-table'

const columns: ColumnDef<FastboundCsvRowDiff>[] = [
    {
        accessorKey: 'rowNumber',
        header: 'Row #',
        cell: ({ row }) => <span className='font-mono text-xs'>{row.getValue('rowNumber')}</span>,
    },
    {
        accessorKey: 'fflNumber',
        header: 'FFL Number',
        cell: ({ row }) => <span className='font-mono text-xs'>{row.getValue('fflNumber') || '—'}</span>,
    },
    {
        accessorKey: 'invoiceId',
        header: 'Invoice',
        cell: ({ row }) => <span className='font-mono text-xs'>{row.getValue('invoiceId') || '—'}</span>,
    },
    {
        accessorKey: 'priceNickname',
        header: 'Price Nickname',
        cell: ({ row }) => <span className='font-mono text-xs'>{row.getValue('priceNickname') || '—'}</span>,
    },
    {
        accessorKey: 'reason',
        header: 'Reason',
        cell: ({ row }) => <span className='text-sm'>{row.getValue('reason')}</span>,
    },
    {
        accessorKey: 'rawValues',
        header: 'Raw Values',
        cell: ({ row }) => <span className='text-xs text-muted-foreground break-all'>{(row.getValue('rawValues') as string[]).join(' | ')}</span>,
    },
]

export default function FastboundDiffReport() {
    const { data: diffRows = [], isLoading, refetch, isRefetching } = useFastboundDiff()
    const [pagination, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 25 })

    React.useEffect(() => {
        const totalPages = Math.max(Math.ceil(diffRows.length / pagination.pageSize), 1)
        if (pagination.pageIndex > totalPages - 1) {
            setPagination((prev) => ({ ...prev, pageIndex: Math.max(totalPages - 1, 0) }))
        }
    }, [diffRows.length, pagination.pageIndex, pagination.pageSize])

    const paginatedData = React.useMemo(() => {
        const start = pagination.pageIndex * pagination.pageSize
        return diffRows.slice(start, start + pagination.pageSize)
    }, [diffRows, pagination])

    const handleExport = () => {
        if (!diffRows.length) return
        const headers = ['Row #', 'FFL Number', 'Invoice', 'Price Nickname', 'Reason', 'Raw Values']
        const rows = diffRows.map((row) => [
            row.rowNumber,
            row.fflNumber,
            row.invoiceId,
            row.priceNickname,
            row.reason.replace(/"/g, '""'),
            `"${row.rawValues.join('|').replace(/"/g, '""')}"`,
        ])

        const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `fastbound_diff_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <div>
                            <CardTitle>Unmatched Fastbound</CardTitle>
                            <p className='text-sm text-muted-foreground mt-1'>
                                Rows from the latest Fastbound CSV that don’t match an existing 4473 Cloud store.
                            </p>
                        </div>
                        <div className='flex items-center gap-2'>
                            <Button variant='outline' size='sm' onClick={() => refetch()} disabled={isLoading || isRefetching}>
                                <RefreshCw className='h-4 w-4 mr-2' />
                                Refresh
                            </Button>
                            <Button variant='outline' size='sm' onClick={handleExport} disabled={!diffRows.length}>
                                <Download className='h-4 w-4 mr-2' />
                                Export CSV
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className='text-sm text-muted-foreground'>Loading Fastbound diff…</div>
                    ) : diffRows.length === 0 ? (
                        <div className='text-sm text-muted-foreground'>No mismatched rows found in the latest Fastbound CSV.</div>
                    ) : (
                        <div className='space-y-3'>
                            <div className='text-sm text-muted-foreground'>
                                {diffRows.length} row{diffRows.length !== 1 ? 's' : ''} require attention.
                            </div>
                            <DataTable
                                columns={columns}
                                data={paginatedData}
                                isLoading={isLoading}
                                enablePagination={true}
                                serverPagination={true}
                                pageIndex={pagination.pageIndex}
                                pageSize={pagination.pageSize}
                                pageCount={Math.max(Math.ceil(diffRows.length / pagination.pageSize), 1)}
                                onPaginationChange={(updater) => {
                                    setPagination((prev) =>
                                        typeof updater === 'function' ? updater(prev) : updater
                                    )
                                }}
                                rowCountColumn
                                scrollContainerClassName='max-h-[calc(100vh-400px)] overflow-y-auto'
                            />
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}